import type { ApolloCache, ApolloClient, ApolloQueryResult, DocumentNode } from '@apollo/client';
import type { Cache } from '@apollo/client/cache/core/types/Cache';

import { compose } from 'common/helpers/fnHelpers';
import recursiveObjectTransform from 'common/helpers/recursiveObjectTransform';
import type { OptionalKeys } from 'common/types';

import { ApolloFetchPolicy } from './constants';

/**
 * A utility that waits for an Apollo query to finish loading.
 *
 * @param client The ApolloClient instance
 * @param query Query which is currently loading.
 * @param variables The variables with which the query was called.
 * @returns A promise which resolves when the query is not in loading state anymore.
 */
export const waitToFinishLoading = <QueryResponse>(
  client: ApolloClient<unknown>,
  query: DocumentNode,
  variables: Record<string, unknown> | undefined
): Promise<void> =>
  new Promise((resolve, reject) => {
    const watchSubscription = client
      .watchQuery({
        query,
        variables,
        fetchPolicy: ApolloFetchPolicy.NETWORK_ONLY,
      })
      .subscribe({
        next: (queryResult: ApolloQueryResult<QueryResponse>) => {
          if (!queryResult.loading) {
            watchSubscription.unsubscribe();
            resolve();
          }
        },
        error: reject,
      });
  });

/**
 * Prior to Apollo Client 3.3, `readQuery` would throw `MissingFieldError` exceptions to report missing fields.
 * Beginning with Apollo Client 3.3, `readQuery` always returns `null` to indicate fields were missing.
 *
 * This function mimics the old functionality.
 *
 * @param cache
 * @param options
 */
export const readQueryAndThrowMissingField = <QueryResponse>(
  cache: ApolloCache<object>,
  options: OptionalKeys<Cache.DiffOptions, 'optimistic'>
) => {
  const diffResult = cache.diff<QueryResponse>({ optimistic: false, ...options });

  // throw MissingFieldError
  if (diffResult.missing && diffResult.missing[0]) {
    throw diffResult.missing[0] as unknown as Error;
  }

  return diffResult.result;
};

/**
 * Returns a new copy of the object that does not contain the __typename field.
 *
 * @param value The object to transform.
 */
export function removeTypenameFromObject<T>(value: T): T {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { __typename, ...rest } = { ...(value as Record<string, unknown>), __typename: '' };
  return rest as T;
}

/**
 * Removes the Symbol properties injected by the Apollo client.
 *
 * @param value The object to remove the property from.
 */
export function removeSymbolsFromObject<T = Record<string, unknown>>(value: T): T {
  return Object.keys(value || {}).reduce(
    (acc, key) => (typeof value[key] !== 'symbol' ? { ...acc, [key]: value[key] } : acc),
    {}
  ) as T;
}

/**
 * Removes all the properties injected by the Apollo client from the given object and returns a new object.
 *
 * @param value The object to transform.
 */
export function removeApolloInjectedFields<T>(value: T) {
  return compose(removeSymbolsFromObject, removeTypenameFromObject)(value) as T;
}

/**
 * Removes the __typename field from the object, including the nested objects
 *
 * @param value The object to transform
 */
export function removeApolloInjectedFieldsRecursive<T>(value: T): T {
  return recursiveObjectTransform(value, removeApolloInjectedFields) as T;
}
